import React, { useState, useEffect, useRef } from 'react';

const InteractiveAddressMap = ({ onAddressSelect, initialPosition = { lat: -6.2088, lng: 106.8456 } }) => {
  const [markerPosition, setMarkerPosition] = useState(initialPosition);
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState('');
  const [zoom, setZoom] = useState(13);
  const [mapLoaded, setMapLoaded] = useState(false);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);

  // Load Leaflet CSS and JS dynamically
  useEffect(() => {
    if (!document.getElementById('leaflet-css')) {
      const css = document.createElement('link');
      css.id = 'leaflet-css';
      css.rel = 'stylesheet';
      css.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(css);
    }

    if (!window.L) {
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.onload = () => setMapLoaded(true);
      document.head.appendChild(script);
    } else {
      setMapLoaded(true);
    }
  }, []);

  // Initialize map after Leaflet is loaded
  useEffect(() => {
    if (mapLoaded && mapRef.current && !mapInstanceRef.current) {
      const map = window.L.map(mapRef.current, {
        center: [markerPosition.lat, markerPosition.lng],
        zoom,
        zoomControl: false,
      });

      window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors',
        maxZoom: 19,
      }).addTo(map);

      const customIcon = window.L.divIcon({
        className: 'custom-marker',
        html: `
          <div style="
            position: relative;
            width: 32px;
            height: 32px;
          ">
            <div style="
              width: 32px;
              height: 32px;
              background-color: #1E9B4C;
              border: 4px solid white;
              border-radius: 50%;
              box-shadow: 0 4px 8px rgba(0,0,0,0.3);
              display: flex;
              align-items: center;
              justify-content: center;
            ">
              <div style="
                width: 12px;
                height: 12px;
                background-color: white;
                border-radius: 50%;
              "></div>
            </div>
          </div>
        `,
        iconSize: [32, 40],
        iconAnchor: [16, 32],
      });

      const marker = window.L.marker([markerPosition.lat, markerPosition.lng], {
        icon: customIcon,
        draggable: true,
      }).addTo(map);

      marker.on('dragstart', () => setIsDragging(true));

      marker.on('drag', (e) => {
        const pos = e.target.getLatLng();
        setMarkerPosition({ lat: pos.lat, lng: pos.lng });
      });

      marker.on('dragend', async (e) => {
        setIsDragging(false);
        const pos = e.target.getLatLng();
        const newPos = { lat: pos.lat, lng: pos.lng };
        setMarkerPosition(newPos);
        try {
          const addressData = await reverseGeocode(newPos.lat, newPos.lng);
          setSelectedAddress(addressData.address);
          if (onAddressSelect) onAddressSelect({ ...addressData, coordinates: newPos });
        } catch (err) {
          console.error('Failed to get address:', err);
        }
      });

      map.on('click', async (e) => {
        if (isDragging) return;
        const newPos = { lat: e.latlng.lat, lng: e.latlng.lng };
        marker.setLatLng([newPos.lat, newPos.lng]);
        setMarkerPosition(newPos);
        try {
          const addressData = await reverseGeocode(newPos.lat, newPos.lng);
          setSelectedAddress(addressData.address);
          if (onAddressSelect) onAddressSelect({ ...addressData, coordinates: newPos });
        } catch (err) {
          console.error('Failed to get address:', err);
        }
      });

      map.on('zoomend', () => setZoom(map.getZoom()));

      mapInstanceRef.current = map;
      markerRef.current = marker;

      // Initial reverse geocode
      reverseGeocode(markerPosition.lat, markerPosition.lng).then((addressData) => {
        setSelectedAddress(addressData.address);
        if (onAddressSelect) onAddressSelect({ ...addressData, coordinates: markerPosition });
      });
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
        markerRef.current = null;
      }
    };
  }, [mapLoaded]);

  // Reverse geocoding function using Nominatim
  const reverseGeocode = async (lat, lng) => {
    setIsLoading(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&addressdetails=1&accept-language=id,en`
      );
      if (!res.ok) throw new Error('Geocoding failed');
      const data = await res.json();
      const addr = data.address || {};
      const displayName = data.display_name || 'Alamat tidak ditemukan';
      setIsLoading(false);
      return {
        address: displayName,
        city: addr.city || addr.town || addr.village || addr.county || 'Tidak diketahui',
        province: addr.state || addr.province || 'Tidak diketahui',
        district: addr.suburb || addr.city_district || addr.district || 'Tidak diketahui',
        village: addr.village || addr.neighbourhood || addr.hamlet || 'Tidak diketahui',
        postalCode: addr.postcode || '-',
        country: addr.country || 'Indonesia',
      };
    } catch (error) {
      console.error('Reverse geocoding error:', error);
      setIsLoading(false);
      return {
        address: `Lokasi di koordinat ${lat.toFixed(6)}, ${lng.toFixed(6)}`,
        city: 'Jakarta',
        province: 'DKI Jakarta',
        district: 'Tidak diketahui',
        village: 'Tidak diketahui',
        postalCode: '-',
        country: 'Indonesia',
      };
    }
  };

  const handleZoomIn = () => {
    if (mapInstanceRef.current && zoom < 19) mapInstanceRef.current.zoomIn();
  };

  const handleZoomOut = () => {
    if (mapInstanceRef.current && zoom > 1) mapInstanceRef.current.zoomOut();
  };

  const resetToCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation tidak didukung oleh browser Anda.');
      return;
    }
    setIsLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const newPos = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setMarkerPosition(newPos);
        if (mapInstanceRef.current && markerRef.current) {
          mapInstanceRef.current.setView([newPos.lat, newPos.lng], zoom);
          markerRef.current.setLatLng([newPos.lat, newPos.lng]);
          try {
            const addressData = await reverseGeocode(newPos.lat, newPos.lng);
            setSelectedAddress(addressData.address);
            if (onAddressSelect) onAddressSelect({ ...addressData, coordinates: newPos });
          } catch (err) {
            console.error('Failed to get address:', err);
          }
        }
        setIsLoading(false);
      },
      (err) => {
        console.error('Geolocation error:', err);
        setIsLoading(false);
        alert('Tidak dapat mengakses lokasi Anda. Pastikan izin lokasi telah diberikan.');
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#dfe4e0' }}>
      <div className="p-4 md:p-6 space-y-4 md:space-y-6">
        {/* Map Container - Responsive height */}
        <div className="relative bg-gray-200 rounded-2xl overflow-hidden shadow-lg">
          <div 
            ref={mapRef} 
            className="relative w-full h-80 sm:h-80 md:h-96" 
            style={{ minHeight: '320px', maxHeight: '400px' }}
          >
            {!mapLoaded && (
              <div className="absolute inset-0 bg-gray-300 flex items-center justify-center">
                <div className="flex items-center space-x-3 text-gray-700">
                  <div className="animate-spin w-6 h-6 border-2 border-[#1E9B4C] border-t-transparent rounded-full"></div>
                  <span className="font-semibold text-sm md:text-base">Memuat peta...</span>
                </div>
              </div>
            )}
          </div>

          {/* Map Controls - Mobile responsive */}
          <div className="absolute top-3 right-3 md:top-4 md:right-4 space-y-2 z-1000" style={{ zIndex: 1000 }}>
            <button
              onClick={resetToCurrentLocation}
              className="w-10 h-10 md:w-11 md:h-11 bg-gray-800 border border-gray-600 rounded-lg shadow-lg flex items-center justify-center hover:bg-gray-700 transition-colors disabled:opacity-50 text-white"
              title="Gunakan lokasi saya"
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="animate-spin w-4 h-4 md:w-5 md:h-5 border-2 border-[#1E9B4C] border-t-transparent rounded-full"></div>
              ) : (
                <span className="text-sm md:text-base">📍</span>
              )}
            </button>
          </div>

          {/* Zoom Controls - Mobile responsive */}
          <div className="absolute bottom-3 right-3 md:bottom-4 md:right-4 space-y-1 z-1000" style={{ zIndex: 1000 }}>
            <button
              onClick={handleZoomIn}
              className="w-9 h-9 md:w-10 md:h-10 bg-gray-800 border border-gray-600 rounded-t shadow-lg flex items-center justify-center hover:bg-gray-700 transition-colors text-base md:text-lg font-bold text-white disabled:opacity-50"
              disabled={zoom >= 19}
            >
              +
            </button>
            <button
              onClick={handleZoomOut}
              className="w-9 h-9 md:w-10 md:h-10 bg-gray-800 border border-gray-600 rounded-b shadow-lg flex items-center justify-center hover:bg-gray-700 transition-colors text-base md:text-lg font-bold text-white disabled:opacity-50"
              disabled={zoom <= 1}
            >
              −
            </button>
          </div>

          {/* Zoom level indicator - Mobile responsive */}
          <div className="absolute top-3 left-3 md:top-4 md:left-4 bg-black bg-opacity-60 text-white px-2 py-1 md:px-3 md:py-1.5 rounded-lg text-xs md:text-sm font-semibold z-1000" style={{ zIndex: 1000 }}>
            Zoom: {zoom}
          </div>

          {/* Loading Overlay */}
          {isLoading && (
            <div className="absolute inset-0 bg-gray-900 bg-opacity-80 flex items-center justify-center backdrop-blur-sm z-1000" style={{ zIndex: 1000 }}>
              <div className="flex items-center space-x-3 bg-gray-800 rounded-lg px-4 py-3 md:px-6 md:py-4 shadow-xl border border-gray-600 mx-4">
                <div className="animate-spin w-5 h-5 md:w-6 md:h-6 border-3 border-[#1E9B4C] border-t-transparent rounded-full"></div>
                <span className="font-semibold text-gray-200 text-sm md:text-base">Mencari alamat...</span>
              </div>
            </div>
          )}
        </div>

        {/* Address Display - Mobile responsive */}
        <div className="bg-gray-800 rounded-2xl p-4 md:p-6 border border-gray-700">
          <div className="flex items-start space-x-3 md:space-x-4">
            <div className="w-5 h-5 md:w-6 md:h-6 text-[#1E9B4C] mt-0.5 flex-shrink-0">📍</div>
            <div className="flex-1">
              <h4 className="font-semibold text-gray-100 mb-2 text-sm md:text-base">Alamat Terpilih</h4>
              <p className="text-gray-300 leading-relaxed text-sm md:text-base">
                {selectedAddress || 'Klik pada peta atau geser marker hijau untuk memilih alamat'}
              </p>
              {selectedAddress && (
                <div className="mt-3 md:mt-4 p-2 md:p-3 bg-gradient-to-r from-[#1E9B4C]/20 to-[#1E9B4C]/20 rounded-lg border border-[#1E9B4C]/30">
                  <div className="text-xs md:text-sm font-mono text-gray-300">
                    <span className="font-semibold text-[#1E9B4C]">Koordinat:</span> {markerPosition.lat.toFixed(6)}, {markerPosition.lng.toFixed(6)}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Instructions - Mobile responsive */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 md:p-5 border border-blue-200">
          <div className="flex items-center mb-3 md:mb-4">
            <div className="w-7 h-7 md:w-8 md:h-8 bg-green-500 rounded-full flex items-center justify-center mr-3">
              <svg className="w-3 h-3 md:w-4 md:h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <h4 className="font-semibold text-green-900 text-base md:text-lg">Cara Menggunakan Peta</h4>
          </div>

          {/* Grid Instruksi - Mobile responsive */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="bg-white rounded-lg shadow-sm p-3 md:p-4 flex items-start space-x-3 hover:shadow-md transition-shadow">
              <div className="w-8 h-8 md:w-10 md:h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-green-600 font-bold text-sm md:text-base">1</span>
              </div>
              <p className="text-gray-700 text-xs md:text-sm leading-relaxed">Klik di mana saja pada peta untuk memindahkan marker</p>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm p-3 md:p-4 flex items-start space-x-3 hover:shadow-md transition-shadow">
              <div className="w-8 h-8 md:w-10 md:h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-green-600 font-bold text-sm md:text-base">2</span>
              </div>
              <p className="text-gray-700 text-xs md:text-sm leading-relaxed">Drag & drop marker hijau untuk posisi yang lebih presisi</p>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm p-3 md:p-4 flex items-start space-x-3 hover:shadow-md transition-shadow">
              <div className="w-8 h-8 md:w-10 md:h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-blue-600 font-bold text-sm md:text-base">3</span>
              </div>
              <p className="text-gray-700 text-xs md:text-sm leading-relaxed">Gunakan kontrol zoom (+/-) untuk melihat detail area</p>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm p-3 md:p-4 flex items-start space-x-3 hover:shadow-md transition-shadow">
              <div className="w-8 h-8 md:w-10 md:h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-green-600 font-bold text-sm md:text-base">4</span>
              </div>
              <p className="text-gray-700 text-xs md:text-sm leading-relaxed">Tombol lokasi 📍 untuk kembali ke posisi Anda</p>
            </div>
          </div>

          {/* Tips - Mobile responsive */}
          <div className="mt-3 md:mt-4 bg-green-100 border border-green-200 rounded-md p-3 text-xs md:text-sm text-blue-700 flex items-start">
            <svg className="w-4 h-4 md:w-5 md:h-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <p><strong>Tips:</strong> Gunakan zoom in untuk melihat detail jalan & bangunan lebih akurat</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InteractiveAddressMap;